import React from 'react';
import { CreateJobDto } from '../../../../../services/applicant_tracking/JobService/CreateJobDto';
import { GlobalState, useStateMachine } from 'little-state-machine';
import { JobDetails } from '../../../../../services/applicant_tracking/JobService/EditJobDto';
import { JobAndJobPost, initCreateJobDto } from '..';
import { getReferrer } from '../referrers';
import { useForm } from 'react-hook-form';
import { handleFormErrors } from '../../../../../services/ApiService/handler';
import { JobService } from '../../../../../services/applicant_tracking/JobService';
import { Card } from '../../../../../components/Card';
import { JobOrganicPostsHeader } from './JobOrganicPostsHeader';
import { Col, Row } from 'reactstrap';
import { JobOrganicPostsToggle } from './JobOrganicPostsToggle';
import { AlertMessage } from '../../../../../components/AlertMessage';
import { JobOrganicPostsDetails } from './JobOrganicPostsDetails';

interface PropTypes {
  setupStep: number;
  setSetupStep: (newStep: number, jobDTO: CreateJobDto) => void;
  updateLocalStorage: (state: GlobalState, payload: any) => GlobalState;
  initJobDto: CreateJobDto;
  isEditFlow: boolean;
  jobDetails: JobDetails;
  onSubmit: (state: CreateJobDto) => Promise<JobAndJobPost>;
}

function OrganicPostsAlert() {
  return (
    <AlertMessage
      hasOverlay={false}
      icon={{ name: 'bi-info-circle' }}
      className='fs-5 bg-light-warning'
      text={
        'Select the job boards to post your job. Organic feeds are free and boost visibility to potential applicants. Keep in mind that some channels may require additional setup.'
      }
    />
  );
}

export function SetupJobOrganicPostsPage(props: PropTypes) {
  const updateLocalStorage = props.updateLocalStorage;
  const { actions, state } = useStateMachine({ updateLocalStorage });

  const { handleSubmit, formState, getValues, setError } =
    useForm<CreateJobDto>({
      mode: 'onChange',
      defaultValues: props.initJobDto,
    });

  const resetAndExit = () => {
    actions.updateLocalStorage(initCreateJobDto);

    window.location.href = getReferrer();
  };

  const submitData = async (data: CreateJobDto) => {
    try {
      await props.onSubmit({ ...data });
      actions.updateLocalStorage({ ...getValues() });

      return true;
    } catch (e: unknown) {
      handleFormErrors(e, setError);
    }

    return false;
  };

  const onSubmit = handleSubmit(async (data: CreateJobDto) => {
    const isSuccessful = await submitData(data);
    if (!isSuccessful) return;

    await JobService.publish(state.setupJob.jobId.toString(), true);
    resetAndExit();
  });

  const onSave = handleSubmit(async (data: CreateJobDto) => {
    const isSuccessful = await submitData(data);
    if (!isSuccessful) return;

    await JobService.submit(state.setupJob.jobId.toString());
    resetAndExit();
  });

  const onNavigate = () => {
    actions.updateLocalStorage({ ...getValues() });
  };

  return (
    <Card>
      <JobOrganicPostsHeader
        organicPostsEnabled={false}
        formState={formState}
        {...props}
        onCancel={() => {
          actions.updateLocalStorage(initCreateJobDto);
          window.location.href = getReferrer();
        }}
        onSave={onSave}
        onSubmit={onSubmit}
        jobDetails={props.jobDetails}
        onNavigate={onNavigate}
      />
      <div className='d-flex flex-column gap-2h mt-4'>
        <OrganicPostsAlert />
        <Row>
          <Col xs='8'>
            <JobOrganicPostsToggle />
          </Col>
          <Col xs='4'>
            <JobOrganicPostsDetails />
          </Col>
        </Row>
      </div>
    </Card>
  );
}
